import { useState } from 'react';
import { useBranch } from '../../context/BranchContext';
import { Building2, ChevronDown, Check, MapPin, Lock } from 'lucide-react';
import { Button } from '../ui/button';

const BranchSelector = () => {
  const { branches, currentBranch, selectBranch, loading, canSwitchBranch } = useBranch();
  const [isOpen, setIsOpen] = useState(false);

  if (loading || !currentBranch) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
        <Building2 className="w-4 h-4 text-muted-foreground animate-pulse" />
        <span className="text-sm text-muted-foreground">Cargando...</span>
      </div>
    );
  }

  // If user cannot switch branches, show a non-interactive indicator
  if (!canSwitchBranch) {
    return (
      <div 
        className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
          currentBranch.is_matriz 
            ? 'border border-accent bg-accent/5 text-accent' 
            : 'border border-blue-400 bg-blue-50 text-blue-700'
        }`}
        title="Sucursal asignada - No puede cambiar"
      >
        <MapPin className="w-4 h-4" />
        <span className="font-medium text-sm max-w-[150px] truncate">
          {currentBranch.name}
        </span>
        <Lock className="w-3 h-3 opacity-50" />
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        className={`flex items-center gap-2 px-3 py-2 h-auto ${
          currentBranch.is_matriz 
            ? 'border-accent bg-accent/5 text-accent' 
            : 'border-blue-400 bg-blue-50 text-blue-700'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="branch-selector"
      >
        <MapPin className="w-4 h-4" />
        <span className="font-medium text-sm max-w-[150px] truncate">
          {currentBranch.name}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full mt-2 right-0 z-50 w-64 bg-white rounded-lg shadow-lg border border-border overflow-hidden">
            <div className="p-2 border-b bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Selecciona Sucursal
              </p>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {branches.filter(b => b.is_active !== false).map((branch) => (
                <button
                  key={branch.branch_id}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors ${
                    currentBranch.branch_id === branch.branch_id ? 'bg-accent/10' : ''
                  }`}
                  onClick={() => {
                    selectBranch(branch);
                    setIsOpen(false);
                  }}
                  data-testid={`branch-option-${branch.branch_id}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    branch.is_matriz 
                      ? 'bg-accent/20 text-accent' 
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {branch.is_matriz ? (
                      <Building2 className="w-4 h-4" />
                    ) : (
                      <MapPin className="w-4 h-4" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{branch.name}</p>
                    {branch.is_matriz && (
                      <p className="text-xs text-accent">Principal</p>
                    )}
                  </div>
                  
                  {currentBranch.branch_id === branch.branch_id && (
                    <Check className="w-4 h-4 text-accent flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
            
            <div className="p-2 border-t bg-muted/30">
              <p className="text-[10px] text-muted-foreground text-center">
                Los datos se filtran por sucursal seleccionada
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BranchSelector;
